import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { api } from '@/app/api';

const initialState = {
  profile: null,
  branches: [],
  activeBranchId: null,
  status: 'idle',
  error: null,
  sessionChecked: false,
};

export const restoreCrmSession = createAsyncThunk('crmAuth/restoreSession', async () => {
  await api.get('/auth/crm/verify');
});

export const loginManager = createAsyncThunk(
  'crmAuth/login',
  async ({ ownerId, login, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/crm/login', { ownerId, login, password });
      return data.profile;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message ?? 'Ошибка входа');
    }
  },
);

/** Сессия CRM по уже активной сессии владельца (тот же ownerId в URL). */
export const bootstrapCrmFromOwner = createAsyncThunk(
  'crmAuth/bootstrapFromOwner',
  async ({ ownerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/crm/bootstrap-from-owner', { ownerId });
      return data.profile;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message ?? null);
    }
  },
);

export const logoutManager = createAsyncThunk('crmAuth/logout', async () => {
  await api.post('/auth/crm/logout').catch(() => undefined);
  Cookies.remove('crm_token');
});

export const loadCrmProfile = createAsyncThunk(
  'crmAuth/loadProfile',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/auth/crm/me');
      return data.profile;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message ?? 'Не удалось загрузить профиль');
    }
  },
);

const crmAuthSlice = createSlice({
  name: 'crmAuth',
  initialState,
  reducers: {
    setActiveBranch(state, action) {
      state.activeBranchId = action.payload;
    },
    resetCrmAuth: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginManager.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginManager.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.sessionChecked = true;
        state.profile = action.payload;
        state.branches = action.payload.branches ?? [];
      })
      .addCase(loginManager.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      })
      .addCase(bootstrapCrmFromOwner.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.sessionChecked = true;
        state.profile = action.payload;
        state.branches = action.payload.branches ?? [];
        state.error = null;
      })
      .addCase(bootstrapCrmFromOwner.rejected, () => {
        /* тихий отказ — остаётся форма входа в CRM */
      })
      .addCase(restoreCrmSession.fulfilled, (state) => {
        state.sessionChecked = true;
        if (state.status === 'loading') return;
        state.status = 'authenticated';
      })
      .addCase(restoreCrmSession.rejected, (state) => {
        state.sessionChecked = true;
        if (state.status === 'loading' || state.status === 'authenticated') return;
        state.status = 'idle';
        state.profile = null;
        state.branches = [];
      })
      .addCase(logoutManager.fulfilled, () => initialState)
      .addCase(loadCrmProfile.fulfilled, (state, action) => {
        if (!action.payload) {
          state.status = 'idle';
          state.profile = null;
          return;
        }
        state.status = 'authenticated';
        state.profile = action.payload;
        state.branches = action.payload.branches ?? [];
      })
      .addCase(loadCrmProfile.rejected, (state) => {
        state.status = 'idle';
        state.profile = null;
      });
  },
});

export const { setActiveBranch, resetCrmAuth } = crmAuthSlice.actions;
export default crmAuthSlice.reducer;
